// ============================================================================
// Form-option hooks: facility picker + employee picker for assigned_to.
// Same pattern as sub-replacement, scoped to active rows. RLS scopes
// facilities to is_my_facility automatically for AMs.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

export interface FacilityOption {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  market_region: string | null;
  // AFB-0057 (2c) — so the assignee picker can pin the facility's own account
  // manager to the top. eid is the single source of truth for AM identity
  // (account_manager_name was dropped 2026-06-08).
  account_manager_eid: string | null;
}

export interface EmployeeOption {
  employee_id: string;
  employee_name_first: string | null;
  employee_name_last: string | null;
}

export function useFacilityOptions(): {
  facilities: FacilityOption[];
  isLoading: boolean;
  error: string | null;
} {
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('active_facilities')
          .select('facility_id, facility_name, account_id, market_region, account_manager_eid')
          .order('facility_name');
        if (cancelled) return;
        if (e) throw e;
        setFacilities((data ?? []) as FacilityOption[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[tasks] facility options load failed', err);
        setError(
          errorMessage(err, 'Failed to load facilities.', 'tasks:facility-options'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { facilities, isLoading, error };
}

export function useEmployeeOptions(): {
  employees: EmployeeOption[];
  isLoading: boolean;
  error: string | null;
} {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // ⛔ YOU CANNOT ASSIGN A TASK TO SOMEONE WITHOUT APP ACCESS — Ben,
        // 2026-09-02 (TCSD-0165). Temps and anyone who is not full-time staff
        // are not assignable, because a task assigned to someone who cannot
        // open the app is a task nobody will ever see.
        //
        // Binds to v_employee_picker: Active AND pay_type='Salaried' AND
        // app_role IS NOT NULL (TCSD-0152). ⛔ DO NOT WIDEN THIS BACK TO
        // v_employees_am — that view is the FULL roster, temps and Former
        // employees included, and binding a dropdown to it is the mistake this
        // corrects. PICKERS EXCLUDE, RESOLVERS RESOLVE (TCSD-0129).
        //
        // ⛔ AND DO NOT USE THIS LIST TO RENDER A NAME ON AN EXISTING RECORD.
        // That is what useTaskActorNames() below is for. This hook used to do
        // both jobs from one fetch, which is why it could not simply be
        // repointed (TCSD-0163): narrowing it would have printed a bare EID-
        // for any creator or comment author who is off-roster.
        //
        // employment_status is NOT named here: every row in the view is Active
        // by definition, the view has no such column, and ⚠️ a .select() or
        // .eq() naming a missing column fails at RUNTIME in PostgREST, which
        // tsc cannot catch.
        const { data, error: e } = await supabase
          .from('v_employee_picker')
          .select('employee_id, employee_name_first, employee_name_last')
          // AFB-0057 — sort by the name that is actually DISPLAYED. This
          // ordered by employee_name_last from 1dac5f7 (2026-05-14), two months
          // before Jose asked for an alphabetical list, while employeeLabel()
          // renders "First Last". Reading down the first names gave Antonela,
          // Zach, Landon, Melanie… which looks unsorted to a human. The sort was
          // never missing; the sort KEY disagreed with the label.
          .order('employee_name_first')
          .order('employee_name_last');
        if (cancelled) return;
        if (e) throw e;
        setEmployees((data ?? []) as EmployeeOption[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[tasks] employee options load failed', err);
        setError(
          errorMessage(err, 'Failed to load employees.', 'tasks:employee-options'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { employees, isLoading, error };
}

export function employeeLabel(e: EmployeeOption | null | undefined): string {
  if (!e) return '';
  return `${e.employee_name_first ?? ''} ${e.employee_name_last ?? ''}`.trim();
}

/**
 * The RESOLVER half of the pair (TCSD-0163). `useEmployeeOptions()` above
 * answers "who could I assign this to"; this answers "what is this person
 * called", for people already named on a record — the task creator, the actor
 * in a reassign toast, the author of each comment.
 *
 * ⛔ These are two different questions and they must never share one fetch.
 * The assignable list deliberately EXCLUDES temps, non-full-time staff and
 * anyone off-roster; this one deliberately INCLUDES all of them, because a
 * task filed by someone who has since left still has to show their name and
 * not a bare `EID-` string. Narrowing this is a FAIL, not a tidy-up
 * (TCSD-0129, TCSD-0152).
 *
 * Reads `v_am_name`, the house resolver — `employee_id` + `display_name`, wide
 * on purpose, forever — with `.in('employee_id', …)`, the same shape
 * `use-am-routes-data.ts` and `use-separation-data.ts` already use. The
 * `.in()` is also what makes this file classify correctly in
 * `src/shared/employee-view-boundary.test.ts`.
 */
export function useTaskActorNames(eids: (string | null | undefined)[]): Record<string, string> {
  const key = [...new Set(eids.filter((e): e is string => !!e))].sort().join(',');
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('v_am_name')
        .select('employee_id, display_name')
        .in('employee_id', ids);
      if (cancelled) return;
      if (error) {
        // Non-fatal: a missing name must never stop the card rendering. The
        // callers fall back to the raw id, which is ugly but honest.
        console.warn('[tasks] actor name lookup failed', error);
        return;
      }
      const map: Record<string, string> = {};
      for (const r of (data ?? []) as { employee_id: string; display_name: string | null }[]) {
        if (r.display_name) map[r.employee_id] = r.display_name;
      }
      setNames(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return names;
}

/**
 * AFB-0057 (2c) — assignee options in the order a human wants to read them:
 * the facility's own account manager first (they are the likeliest assignee for
 * a facility task), then everyone else alphabetically by the name shown on
 * screen. Pure so it can be tested without a database.
 *
 * The AM keeps their alphabetical position too, so the list is never missing a
 * name from where you'd scan for it.
 */
export interface AssigneeOption extends EmployeeOption {
  /** Short reason this option is pinned, rendered next to the name. */
  pinnedNote?: string;
}

export function orderAssignees(
  employees: EmployeeOption[],
  facilityAmEid: string | null | undefined,
): AssigneeOption[] {
  const byDisplayName = [...employees].sort((a, b) =>
    employeeLabel(a).localeCompare(employeeLabel(b), undefined, { sensitivity: 'base' }),
  );
  if (!facilityAmEid) return byDisplayName;
  const am = byDisplayName.find((e) => e.employee_id === facilityAmEid);
  if (!am) return byDisplayName;
  return [{ ...am, pinnedNote: 'owns this facility' }, ...byDisplayName];
}
